import React, {useMemo, Fragment, useState} from 'react';
import {Button, Sticky, Submit, Group, Form, Edit, Icons, Popup, Flex, List, DynamicContainer} from 'components';
import {over, noop, debounce, includes, each, map, computeDifference, keyBy, useNamedSelector, useEditableList, openPopup, usePrevious, useUpdateEffect} from 'helpers';
import * as store from 'store';

export function CheckIn({ children }) {
  const { all, visible } = useNamedSelector('notes.morningThoughtsCategories');
  return (
    <div>
      <Sticky>
        <Flex className={['middle', 'justified']}>
          { children }
          <Button className="tiny" icon="settings" onClick={openPopup(Visibility)}>
            Categories
          </Button>
        </Flex>
      </Sticky>
      <div>
        <List tiny-gutter>
          { visible.map(category =>
            <CategoryNotes key={category.id} {...category} />
          ) }
        </List>
      </div>
    </div>
  );
}

function CategoryNotes({ id, title, name, predefined }) {
  const notes = useNamedSelector('notes.morningThoughtsNotes', { id }) || [];
  const [ value, setValue ] = useState(notes || []);

  const list = useEditableList(value, setValue, 'text');

  const previousValue = usePrevious(value);

  const debounceUpdateNote = useMemo(() => {
    const stash = {};
    return ({ now }) => {
      stash[now.id] = stash[now.id] || debounce(({now}) => store.notes.dispatch.updateText(now.id, now.text), 1000);
      stash[now.id]({ now });
    }
  }, []);

  useUpdateEffect(() => {
    const { created, updated, deleted } = computeDifference(byId(value), byId(previousValue)) || {};
    each(created, ({model: note}) => store.notes.dispatch.update({
      id: note.id,
      text: note.text,
      category: name,
      morning: true
    }));
    each(updated, debounceUpdateNote);
    each(deleted, ({model: note}) => store.notes.dispatch.remove(note));
  }, [ value ]);

  return (
    <Fragment>
      <Flex middle justified className="smedium-spacing">
        <b>{ title || name || 'Untitled' }</b>
        { !predefined &&
          <Icons.Handle className="secondary small" onClick={openPopup(CategoryContextMenu, { id, name})} />
        }
      </Flex>
      <DynamicContainer>
        <Group className="has-gap">
          { map(value.length ? value : [ { id: 0 } ], (item, index) =>
            <Edit
              index={index}
              key={item.id}
              name={item.id}
              onKeyDown={list.onKeyDown}
              placeholder="Enter note.."
              setValue={list.setValue}
              value={item.text}
            />
          ) }
        </Group>
      </DynamicContainer>
    </Fragment>
  );
}

function CategoryContextMenu({ id, popup }) {
  return (
    <Popup.Menu onClick={noop}>
      <Popup.Menu.Item icon="invisible" onClick={() => toggleCategory({ id })}>Hide</Popup.Menu.Item>
      <Popup.Menu.Item icon="pencil" onClick={popup.redirect(EditCategory)}>Edit</Popup.Menu.Item>
      <Popup.Menu.Separator />
      <Popup.Menu.DeleteItem onConfirm={() => store.notes.dispatch.removeCategory({id})}>Delete</Popup.Menu.DeleteItem>
    </Popup.Menu>
  );
}

export function EditCategory({ id, name, popup }) {
  return (
    <Form initialValues={{ id, name, popup }} onSubmit={over(updateCategory, popup.close)} delay className="compact-popup">
      <Edit name="name" autoFocus required placeholder="Enter category name.." />
      <Submit icon="check" />
    </Form>
  );
}

export function Visibility() {
  const {all, visible} = useNamedSelector('notes.morningThoughtsCategories');
  const isVisible = category => includes(visible, category);
  return (
    <>
      <Popup.Menu onClick={noop} className="is-small">
        { all.map(category =>
          <Popup.Menu.Checkbox key={category.id} checked={isVisible(category)} onClick={() => toggleCategory(category)}>
            { category.title || category.name || 'Untitled' }
          </Popup.Menu.Checkbox>
        ) }
      </Popup.Menu>
      <Popup.Menu onClick={noop}>
        <Popup.Menu.Separator />
        <Popup.Menu.AdvanceItem icon="plus" Component={NewCategory}>New Category</Popup.Menu.AdvanceItem>
      </Popup.Menu>
    </>
  );
}

export function NewCategory({ toggle }) {
  return (
    <Form onSubmit={over(store.notes.dispatch.updateCategory, toggle)} delay className="compact">
      <Edit name="name" autoFocus required placeholder="Enter category name.."  />
      <Submit icon="check" />
    </Form>
  );
}

function byId(notes) {
  return keyBy(notes.filter(note => note.text), 'id');
}

function toggleCategory({ id }) {
  // store.dispatch({
  //   type: 'TODAY_TOGGLE_NOTE_CATEGORY',
  //   id,
  // });
}
